import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Button, Typography, Box, FormControlLabel, Switch } from '@mui/material';
import { Edit } from '@mui/icons-material';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { MemberSimpleSearchModal } from 'src/modules/member/components/memberSimpleSearchModal';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { toast } from 'react-toastify';
import { OrganizationRoleTable } from 'src/shared/components/organisms/organizationRoleTable';
import { OrganizationTitleTypeEnum } from 'src/shared/models/enums/organizationTitleType.enum';
import { OrginizationResignConfirm } from 'src/shared/components/organisms/organizationResignConfirm';
import {
  getAssociationTitleByType,
  getAssociationTitleConfigByType,
  updateAssociationTitle,
  updateAssociationTitleConfig,
} from 'src/shared/services/associationService';
import { getOrganizationAssociationId } from 'src/shared/functions/organizationHelpers';
import { OrganizationTitleEditDateModal } from 'src/shared/components/organisms/organizationTitleEditDateModal';
import { OrganizationTitleConfigModal } from 'src/shared/components/organisms/organizationTitleConfigModal';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { AssociationTitleRequest } from '../models/associationTitleRequest';
import { AssociationTitleResponse } from '../models/associationTitleResponse';
import { InviteUserModal } from '../components/inviteUserModal';

type AssociationTitlePageProps = {
  titleType: OrganizationTitleTypeEnum;
};

export const AssociationTitlePage = (props: AssociationTitlePageProps) => {
  const { titleType } = props;
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState<boolean>(false);
  const { dateStamp, setDateStamp } = useGlobals();

  const { currentUser } = useGlobals();
  const params = useParams();
  const currentAssociationId = getOrganizationAssociationId(currentUser, params.id);

  const [associationTitle, setAssociationTitle] = useState<AssociationTitleResponse[]>([]);
  const [selectedMemberTitleId, setSelectedMemberTitleId] = useState<number>();
  const [selectedMemberTitle, setSelectedMemberTitle] = useState<AssociationTitleResponse>();

  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [showResignModal, setShowResignModal] = useState<boolean>(false);
  const [showTitleConfigModal, setShowTitleConfigModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const [inviteMemberId, setInviteMemberId] = useState<number>();
  const [showHistory, setShowHistory] = useState<boolean>(false);

  const canCreateUser = getPermission(currentUser, PermissionEnum.UserCreateAssociation);

  const isMounted = useIsMounted();

  // Sortera posterna efter titelns position (samma ordning som i redigera-modalen) och lägg
  // historik-rader sist inom varje post, så man ser varje posts historik i logisk ordning.
  const sortedTitles = useMemo(() => {
    return [...associationTitle].sort((a, b) => {
      const byPosition = (a.titlePosition ?? 0) - (b.titlePosition ?? 0);
      if (byPosition !== 0) return byPosition;
      return (a.isHistorical ? 1 : 0) - (b.isHistorical ? 1 : 0);
    });
  }, [associationTitle]);

  useEffect(() => {
    ConsoleHelper.log('current', isMounted, currentAssociationId, dateStamp, titleType);
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getAssociationTitleByType(currentAssociationId, titleType, showHistory);
        if (isMounted()) {
          setAssociationTitle(result.data);
          ConsoleHelper.log(dateStamp);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    fetchData();
  }, [isMounted, currentAssociationId, dateStamp, titleType, showHistory]);

  const handleMemberSelect = async (memberId: number, createUser?: boolean) => {
    setShowUserModal(false);
    const requestData: AssociationTitleRequest = {
      memberTitle_id: selectedMemberTitleId || 0,
      member_id: memberId,
      admDate: memberId > 0 ? DateHelper.getFSDate(new Date()) : '',
      resDate: null,
    };
    try {
      setLoading(true);
      await updateAssociationTitle(currentAssociationId, requestData);
      if (isMounted()) {
        toast.success(<>{t('common:statusMessages.saveChangesSuccess')}</>);
        setDateStamp(new Date());

        // Öppna invite-modalen om admin valt att medlemmen ska få användarkonto.
        if (createUser && memberId > 0) {
          setInviteMemberId(memberId);
        }
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const handleResign = () => {
    handleMemberSelect(0);
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <Box sx={{ p: 3, minHeight: '15rem', maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant='h6' gutterBottom>
            {titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.boardTitle') : t('common:organization.electedTitle')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={<Switch size='small' checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} />}
              label={t('common:organization.showHistory')}
            />
            <Button variant='outlined' size='small' startIcon={<Edit />} onClick={() => setShowTitleConfigModal(true)}>
              {titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoard') : t('common:organization.editElected')}
            </Button>
          </Box>
        </Box>
        {associationTitle && (
          <OrganizationRoleTable<AssociationTitleResponse>
            roles={sortedTitles}
            idColumn='memberTitle_id'
            canUseColumn='canUse'
            titleColumn='title'
            valueColumn='name'
            admDateColumn='admDate'
            resDateColumn='resDate'
            userEmailColumn={canCreateUser ? 'userEmail' : undefined}
            historicalColumn='isHistorical'
            setUserCallback={(titleId) => {
              setSelectedMemberTitleId(titleId);
              setShowUserModal(true);
            }}
            editDateCallback={(titleId) => {
              const selectedTitle = associationTitle.find((x) => x.memberTitle_id === titleId);
              setSelectedMemberTitle(selectedTitle);
              setShowEditModal(true);
            }}
            deleteCallback={(titleId) => {
              setSelectedMemberTitleId(titleId);
              setShowResignModal(true);
            }}
          />
        )}
        {showTitleConfigModal && (
          <OrganizationTitleConfigModal
            modalOpen={showTitleConfigModal}
            onClose={() => setShowTitleConfigModal(false)}
            onSave={() => {
              setShowTitleConfigModal(false);
              setDateStamp(new Date());
            }}
            modalTitle={titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoard') : t('common:organization.editElected')}
            type={titleType}
            organizationId={currentAssociationId}
            fetchMethod={async () => {
              return getAssociationTitleConfigByType(currentAssociationId, titleType);
            }}
            updateMethod={async (data) => {
              return updateAssociationTitleConfig(currentAssociationId, data);
            }}
          />
        )}
        <OrginizationResignConfirm
          modalOpen={showResignModal}
          onClose={() => setShowResignModal(false)}
          onConfirm={() => {
            setShowResignModal(false);
            handleResign();
          }}
        />
        <MemberSimpleSearchModal
          modalOpen={showUserModal}
          onClose={() => setShowUserModal(false)}
          onSelectMember={(memberId, _member, createUser) => handleMemberSelect(memberId, createUser)}
          allowCreateUser={canCreateUser}
        />
        {selectedMemberTitle && showEditModal && (
          <OrganizationTitleEditDateModal
            modalOpen={showEditModal}
            modalTitle={
              titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoardUser') : t('common:organization.editElectedUser')
            }
            organizationTitleResponse={selectedMemberTitle}
            allowCreateUser={canCreateUser && !!selectedMemberTitle.member_id}
            hasUserAccess={selectedMemberTitle.hasUserAccess}
            userEmail={selectedMemberTitle.userEmail}
            onCreateUser={() => {
              setShowEditModal(false);
              if (selectedMemberTitle.member_id) setInviteMemberId(selectedMemberTitle.member_id);
            }}
            updateMethod={async (data) => {
              return updateAssociationTitle(currentAssociationId, data);
            }}
            onClose={() => setShowEditModal(false)}
            onSave={() => {
              setShowEditModal(false);
              setDateStamp(new Date());
            }}
          />
        )}
        {!!inviteMemberId && (
          <InviteUserModal
            open={!!inviteMemberId}
            associationId={currentAssociationId}
            memberId={inviteMemberId}
            onClose={() => setInviteMemberId(undefined)}
            onSaved={() => {
              setInviteMemberId(undefined);
              setDateStamp(new Date());
            }}
          />
        )}
      </Box>
    </FormLoaderWrapper>
  );
};
